// ---------------------------------------------------------------------------
//  PdfHighlightViewer.jsx
// ---------------------------------------------------------------------------
//  Draws translucent, rounded rectangles over every `boundingRegions` polygon
//  that Azure Document Intelligence returned.  No fixed width – the component
//  watches its own <Page> canvases and lines things up perfectly.
// ---------------------------------------------------------------------------

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import PropTypes from 'prop-types';
import styles from './PdfHighlightViewer.module.css';

/* tell react-pdf where the worker lives (CRA / Vite / Webpack) */
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

/* pleasant pastel palette (55 % α) */
const COLORS = [
  '#ff52528c', '#40c4ff8c', '#69f0ae8c',
  '#ffc4008c', '#d500f98c', '#ff91008c',
];

/* Azure gives INCHES; we stay in inches until the very end */
function PdfHighlightViewer({ url, fieldRegions: incoming }) {
  /* ❶ normalise the payload (works with *old* or *new* server output) */
  const { fields } = useMemo(() => {
    // new shape:  { pageSize:{…}, fields:{ … } }
    if (incoming.pageSize && incoming.fields) return incoming;
    // old shape: the flat dictionary itself
    return { pageSize: null, fields: incoming };
  }, [incoming]);

  /* ❷ bookkeeping for every rendered page */
  const [numPages, setNumPages] = useState(null);
  const [pageRects, setPageRects] = useState({});  // { pageNo: { wPx, hPx } }
  const wrapperRefs = useRef({});                  // per-page DOM <div>

  /* after the PDF outline is known */
  const onDocLoad = ({ numPages }) => setNumPages(numPages);

  /* once a page *canvas* is in the DOM we can measure it accurately */
  const onPageRender = useCallback((page, pageNumber) => {
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        setPageRects(prev => ({
          ...prev,
          [pageNumber]: {
            wPx: e.contentRect.width,
            hPx: e.contentRect.height,
          },
        }));
      }
    });
    const div = wrapperRefs.current[pageNumber];
    if (div) ro.observe(div);
    return () => ro.disconnect();
  }, []);

  /* helper: polygon[8] → CSS-% box (relative to measured pixel rect) */
  const polyToCss = useCallback((poly, rect) => {
    if (!rect) return null;
    const xs = [poly[0], poly[2], poly[4], poly[6]];
    const ys = [poly[1], poly[3], poly[5], poly[7]];

    const leftIn   = Math.min(...xs);
    const rightIn  = Math.max(...xs);
    const topIn    = Math.min(...ys);
    const bottomIn = Math.max(...ys);

    const wIn  = rightIn  - leftIn;
    const hIn  = bottomIn - topIn;

    return {
      left  : `${leftIn   / (rightIn - leftIn + (rect.wPx / rect.hPx) * hIn) * 100}%`,
      top   : `${topIn    / (bottomIn - topIn + (rect.hPx / rect.wPx) * wIn) * 100}%`,
      width : `${(wIn / (rightIn - leftIn + (rect.wPx / rect.hPx) * hIn)) * 100}%`,
      height: `${(hIn / (bottomIn - topIn + (rect.hPx / rect.wPx) * wIn)) * 100}%`,
    };
  }, []);

  /* pre-group overlays by page (memoised) */
  const overlays = useMemo(() => {
    const byPg = {};
    Object.entries(fields).forEach(([_, regs], idx) =>
      regs.forEach(r => {
        const css = polyToCss(r.polygon, pageRects[r.pageNumber]);
        if (!css) return;
        (byPg[r.pageNumber] ??= []).push({
          css,
          color: COLORS[idx % COLORS.length],
        });
      }),
    );
    return byPg;
  }, [fields, pageRects, polyToCss]);

  /* ─────────── render ─────────── */
  return (
    <div className={styles.container}>
      <Document file={url} onLoadSuccess={onDocLoad} renderMode="canvas" loading="…" >
        {Array.from({ length: numPages || 0 }, (_, i) => {
          const n = i + 1;
          return (
            <div
              key={n}
              className={styles.pageWrapper}
              ref={el => (wrapperRefs.current[n] = el)}
            >
              <Page
                pageNumber={n}
                onRenderSuccess={page => onPageRender(page, n)}
                width={undefined /* let <Page> fill its container */}
              />

              {(overlays[n] || []).map((ov, k) => (
                <div
                  key={k}
                  className={styles.highlight}
                  style={{ ...ov.css, backgroundColor: ov.color }}
                />
              ))}
            </div>
          );
        })}
      </Document>
    </div>
  );
}

PdfHighlightViewer.propTypes = {
  url          : PropTypes.string.isRequired,
  /** either the old flat object *or* the new `{ pageSize, fields }` payload */
  fieldRegions : PropTypes.object.isRequired,
};

export default PdfHighlightViewer;
